import type { NextRouter } from 'next/router'
import type { NavGroup, NavLink, VerticalNavItemsType } from 'src/@core/layouts/types'
import { resolveNavPath } from 'src/@core/layouts/utils'
import { isNavGroup, isNavSectionTitle, type DrillFrame } from './navGuards'
import { isPrimaryNavLinkActive } from './navLinkActive'

/**
 * Builds the rail drill stack so the sidebar matches the current URL (e.g. Menu → Restaurant → …).
 * @param fullNavItems — Full tree (including Home) for route matching.
 * @param menuRootItems — First-frame items (typically Home stripped) shown in the scrollable menu.
 */
export function computeDrillStackForRoute(
  fullNavItems: VerticalNavItemsType,
  menuRootItems: VerticalNavItemsType,
  router: NextRouter,
  id: string | string[] | undefined
): DrillFrame[] {
  const idNorm = Array.isArray(id) ? id[0] : id
  const pathSeg = router.asPath.split('?')[0].replace(/\/$/, '')

  const base: DrillFrame[] = [{ title: 'Menu', items: menuRootItems }]

  /**
   * Product list uses horizontal sub-nav; the rail must stay on the root menu so every primary
   * module remains reachable (no drill-in from the broad `/app/products` active rule).
   */
  if (pathSeg === '/app/products' || pathSeg.startsWith('/app/products/')) {
    return base
  }

  type MatchResult = { chain: NavGroup[] | 'top'; pathLength: number; depth: number }

  function findBestMatch(items: VerticalNavItemsType, ancestorGroups: NavGroup[]): MatchResult | null {
    let best: MatchResult | null = null

    for (const item of items) {
      if (isNavSectionTitle(item)) continue

      if (!isNavGroup(item)) {
        const link = item as NavLink
        const href = resolveNavPath(link.path, idNorm)
        if (href && isPrimaryNavLinkActive(router, href)) {
          const pathLength = href.split('?')[0].replace(/\/$/, '').length
          const depth = ancestorGroups.length
          const chain: NavGroup[] | 'top' = depth === 0 ? 'top' : ancestorGroups
          const candidate = { chain, pathLength, depth }

          if (
            !best ||
            candidate.pathLength > best.pathLength ||
            (candidate.pathLength === best.pathLength && candidate.depth > best.depth)
          ) {
            best = candidate
          }
        }
      } else {
        const g = item as NavGroup
        const ch = (g.children ?? []) as VerticalNavItemsType
        const nested = findBestMatch(ch, [...ancestorGroups, g])
        if (
          nested &&
          (!best ||
            nested.pathLength > best.pathLength ||
            (nested.pathLength === best.pathLength && nested.depth > best.depth))
        ) {
          best = nested
        }
      }
    }

    return best
  }

  const chain = findBestMatch(fullNavItems, [])?.chain ?? null

  if (chain === null || chain === 'top') {
    return base
  }

  for (const g of chain) {
    base.push({ title: g.title, items: (g.children ?? []) as VerticalNavItemsType })
  }

  return base
}
